import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/apiClient";
import { queryKeys } from "@/services/queryClient";
import type { PropertyArtType } from "@/components/PropertyArt";

export type OccupancyStatus =
  | "vacant"
  | "occupied"
  | "problem_tenant"
  | "evicted"
  | "renovating";

export type RentalStrategy =
  | "long_term"
  | "short_term"
  | "corporate"
  | "student";

export type TenantInfo = {
  name: string;
  tenant_type: "student" | "working" | "family" | "company";
  monthly_income: number;
  reliability_bps: number;
  move_in_month: number;
  lease_end_month: number | null;
  unpaid_months_count: number;
  last_paid_month: number | null;
};

export type OwnedPropertyOut = {
  id: number;
  property_id: number;
  acquired_month: number;
  purchase_price: number;
  current_valuation: number;
  current_rent: number;
  occupancy: OccupancyStatus;
  strategy: RentalStrategy;
  is_managed: boolean;
  is_listed_for_sale: boolean;
  listing_price: number | null;
  accumulated_depreciation: number;
  improvement_costs: number;
  condition_score: number;
  renovation_type: string | null;
  renovation_ends_month: number | null;
  /** Denormalized title/location/category; server-provided. */
  category?: PropertyArtType;
  title?: string;
  location?: string;
  /** Present only when property is occupied. */
  tenant?: TenantInfo | null;
};

export function usePortfolio(playerId: number | undefined) {
  return useQuery<OwnedPropertyOut[]>({
    queryKey: playerId ? queryKeys.playerPortfolio(playerId) : ["portfolio", "disabled"],
    queryFn: () => api.get(`/players/${playerId!}/portfolio`),
    enabled: playerId !== undefined,
  });
}

// ── Buy property ────────────────────────────────────────────────────────────

export type BankId = "csob" | "kb" | "ceska_sporitelna" | "moneta" | "raiffeisen";
export type FixationPeriod = 1 | 3 | 5 | 10;
export type InsurancePlan = "basic" | "standard" | "premium";

export type PropertyBuyPayload = {
  property_id: number;
  bank_id?: BankId;
  fixation_years?: FixationPeriod;
  down_payment_pct?: number;     // 10–90
  term_months?: number;          // 180–360
  collateral_ids?: number[];
  insurance_plan?: InsurancePlan;
  due_diligence_acknowledged?: boolean;
};

export function useBuyProperty(playerId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<OwnedPropertyOut, Error, PropertyBuyPayload>({
    mutationFn: (payload) =>
      api.post(`/players/${playerId!}/portfolio/buy`, payload),
    onSuccess: () => {
      if (playerId === undefined) return;
      void qc.invalidateQueries({ queryKey: queryKeys.player(playerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.playerPortfolio(playerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.playerLoans(playerId) });
      void qc.invalidateQueries({ queryKey: ["marketplace"] });
    },
  });
}

// ── Sell / renovate / strategy / management ─────────────────────────────────

export type SellPayload = { instant?: boolean; listing_price?: number | null };

export function useSellProperty(playerId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean; message: string },
    Error,
    { ownedId: number; payload: SellPayload }
  >({
    mutationFn: ({ ownedId, payload }) =>
      api.post(`/players/${playerId!}/portfolio/${ownedId}/sell`, payload),
    onSuccess: () => {
      if (playerId === undefined) return;
      void qc.invalidateQueries({ queryKey: queryKeys.player(playerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.playerPortfolio(playerId) });
    },
  });
}

export type RenovationType =
  | "cosmetic"
  | "smart_home"
  | "kitchen"
  | "bathroom"
  | "energy"
  | "layout"
  | "complete";

export function useRenovateProperty(playerId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean; message: string },
    Error,
    { ownedId: number; renovation_type: RenovationType }
  >({
    mutationFn: ({ ownedId, renovation_type }) =>
      api.post(`/players/${playerId!}/portfolio/${ownedId}/renovate`, {
        renovation_type,
      }),
    onSuccess: () => {
      if (playerId === undefined) return;
      void qc.invalidateQueries({ queryKey: queryKeys.player(playerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.playerPortfolio(playerId) });
    },
  });
}

export function useUpdateStrategy(playerId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean; message: string },
    Error,
    { ownedId: number; strategy: RentalStrategy }
  >({
    mutationFn: ({ ownedId, strategy }) =>
      api.post(`/players/${playerId!}/portfolio/${ownedId}/strategy`, {
        strategy,
      }),
    onSuccess: () => {
      if (playerId === undefined) return;
      void qc.invalidateQueries({ queryKey: queryKeys.playerPortfolio(playerId) });
    },
  });
}

export function useToggleManagement(playerId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; message: string }, Error, { ownedId: number }>({
    mutationFn: ({ ownedId }) =>
      api.post(`/players/${playerId!}/portfolio/${ownedId}/management/toggle`),
    onSuccess: () => {
      if (playerId === undefined) return;
      void qc.invalidateQueries({ queryKey: queryKeys.playerPortfolio(playerId) });
    },
  });
}

export function useEvictTenant(playerId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; message: string }, Error, { ownedId: number }>({
    mutationFn: ({ ownedId }) =>
      api.post(`/players/${playerId!}/portfolio/${ownedId}/evict-tenant`),
    onSuccess: () => {
      if (playerId === undefined) return;
      void qc.invalidateQueries({ queryKey: queryKeys.playerPortfolio(playerId) });
    },
  });
}

export function useFindTenant(playerId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; message: string }, Error, { ownedId: number }>({
    mutationFn: ({ ownedId }) =>
      api.post(`/players/${playerId!}/portfolio/${ownedId}/find-tenant`),
    onSuccess: () => {
      if (playerId === undefined) return;
      void qc.invalidateQueries({ queryKey: queryKeys.playerPortfolio(playerId) });
    },
  });
}

export function useRemindTenant(playerId: number | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; message: string }, Error, { ownedId: number }>({
    mutationFn: ({ ownedId }) =>
      api.post(`/players/${playerId!}/portfolio/${ownedId}/remind-tenant`),
    onSuccess: () => {
      if (playerId === undefined) return;
      void qc.invalidateQueries({ queryKey: queryKeys.player(playerId) });
      void qc.invalidateQueries({ queryKey: queryKeys.playerPortfolio(playerId) });
    },
  });
}
